'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { HelpCircle, ChevronDown, ChevronUp, Mail, FileText, Info } from 'lucide-react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


const faqs = [
  { id: 'faq-1', q: 'What is GlobalAtlas?', a: 'GlobalAtlas is an AI-driven geopolitical and operational risk operating system for global enterprises. It monitors sanctions, cyber threats, maritime disruptions, political instability, and supply-chain risks — connecting geopolitical developments to your company\'s specific operational exposure.' },
  { id: 'faq-2', q: 'How is the Threat Index calculated?', a: 'The Global Threat Index is an AI-weighted composite score combining: Sanctions exposure (20%), Cyber threat activity (20%), Political instability (20%), Maritime/logistics disruption (20%), and Supply-chain/operational exposure (20%). Scores are adjusted upward or downward based on direct company exposure, event severity, and escalation likelihood.' },
  { id: 'faq-3', q: 'What is the Acme Corp profile?', a: 'Acme Corp is the sample company profile used in this beta. All dashboard exposure summaries, alerts, and risk cards are mapped to Acme Corp\'s monitored suppliers, routes, and jurisdictions. You can update the company profile in Settings.' },
  { id: 'faq-4', q: 'Is this platform certified or audited?', a: 'GlobalAtlas is currently in early-access beta. We are not yet SOC 2 certified or formally audited. We are committed to enterprise-grade security practices and will pursue formal certifications as the platform matures.' },
  { id: 'faq-5', q: 'How do I create an account?', a: 'You can sign up directly from the login page. New accounts are created with viewer access. Contact the platform admin to request analyst or elevated access.' },
  { id: 'faq-6', q: 'How often is the risk data updated?', a: 'The risk feed auto-refreshes every 30 seconds from persistent storage. In the full production release, data will be sourced from live intelligence feeds with sub-60 second event detection.' },
];

export default function HelpPage() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  return (
    <AppLayout activeRoute="/help">
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Help & Support</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Documentation, FAQs, and platform guidance</p>
        </div>

        {/* Beta notice */}
        <div className="card-elevated rounded-xl border border-primary/20 bg-primary/5 p-4">
          <div className="flex items-start gap-3">
            <Info size={15} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">GlobalAtlas Beta</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                You are using an early-access beta version of GlobalAtlas. Some features are still in development. If you encounter issues or have feedback, please use the contact form below.
              </p>
            </div>
          </div>
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Risk Dashboard', desc: 'View live threat feed', href: '/ai-risk-dashboard', icon: HelpCircle },
            { label: 'AI Reports', desc: 'Intelligence briefings', href: '/ai-reports', icon: FileText },
            { label: 'Company Analyser', desc: 'Exposure mapping', href: '/company-analyser', icon: HelpCircle },
          ]?.map(({ label, desc, href, icon: Icon }) => (
            <Link key={label} href={href} className="card-elevated rounded-xl border border-border p-4 hover:bg-muted/20 transition-colors block">
              <Icon size={15} className="text-primary mb-2" />
              <p className="text-sm font-medium text-foreground mb-0.5">{label}</p>
              <p className="text-xs text-muted-foreground">{desc}</p>
            </Link>
          ))}
        </div>

        {/* FAQs */}
        <div className="card-elevated rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <p className="text-sm font-semibold text-foreground">Frequently Asked Questions</p>
          </div>
          <div className="divide-y divide-border">
            {faqs?.map(faq => (
              <div key={faq?.id}>
                <button
                  onClick={() => setOpenFaq(openFaq === faq?.id ? null : faq?.id)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/20 transition-colors"
                >
                  <p className="text-sm font-medium text-foreground">{faq?.q}</p>
                  {openFaq === faq?.id ? <ChevronUp size={15} className="text-muted-foreground flex-shrink-0" /> : <ChevronDown size={15} className="text-muted-foreground flex-shrink-0" />}
                </button>
                {openFaq === faq?.id && (
                  <div className="px-4 pb-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">{faq?.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div className="card-elevated rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-3">
            <Mail size={15} className="text-primary" />
            <p className="text-sm font-semibold text-foreground">Contact Support</p>
          </div>
          <p className="text-xs text-muted-foreground mb-3">For beta feedback, bug reports, or feature requests, reach out to the GlobalAtlas team.</p>
          <a href="mailto:support@globalatlas.io" className="text-sm text-primary hover:text-primary/80 font-medium transition-colors">
            support@globalatlas.io
          </a>
        </div>
      </div>
    </AppLayout>
  );
}
